import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Activity } from '../../../types/Activity';
import { activityService, CreateActivityData } from '../../../services/activityService';
import ActivityTable from '../components/ActivityTable';
import ActivityModal from '../components/ActivityModal';
import ActivityFilters from '../components/ActivityFilters';
import ServerPagination from '../../../components/ServerPagination';
import { usePermissions } from '../../../hooks/usePermissions';
import { Spinner, PageHeader } from '@/components/ui';

const PAGE_SIZE_OPTIONS = [20, 50, 100];
const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_SIZE = 20;
const DEFAULT_SORT_FIELD = 'activityNumber';

type SortDirection = 'asc' | 'desc';

const ActivityView: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize query state from the URL (once, on mount).
  const initialPage = (() => {
    const p = parseInt(searchParams.get('page') || String(DEFAULT_PAGE), 10);
    return p >= 1 ? p : DEFAULT_PAGE;
  })();
  const initialPageSize = (() => {
    const ps = parseInt(searchParams.get('pageSize') || String(DEFAULT_PAGE_SIZE), 10);
    return PAGE_SIZE_OPTIONS.includes(ps) ? ps : DEFAULT_PAGE_SIZE;
  })();

  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState<Activity | undefined>(undefined);
  const [profiles, setProfiles] = useState<string[]>([]);

  const [filters, setFilters] = useState({
    classification: searchParams.get('classification') || '',
    profile: searchParams.get('profile') || '',
  });
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [sort, setSort] = useState<{ field: string; direction: SortDirection }>({
    field: searchParams.get('sortField') || DEFAULT_SORT_FIELD,
    direction: searchParams.get('sortDirection') === 'desc' ? 'desc' : 'asc',
  });
  const [pagination, setPagination] = useState({
    page: initialPage,
    pageSize: initialPageSize,
    totalCount: 0,
    totalPages: 0,
  });

  const { hasPermission } = usePermissions();

  // Debounce the raw search input so we only fetch after the user pauses typing.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(timer);
  }, [search]);

  // Load distinct profiles once (for the Profile filter dropdown).
  useEffect(() => {
    activityService
      .getProfileValues()
      .then(setProfiles)
      .catch(error => console.error('Error fetching profiles:', error));
  }, []);

  // Keep the URL query string in sync with the current filter/search/sort/paging state.
  useEffect(() => {
    const params = new URLSearchParams();
    if (pagination.page !== DEFAULT_PAGE) params.set('page', String(pagination.page));
    if (pagination.pageSize !== DEFAULT_PAGE_SIZE) params.set('pageSize', String(pagination.pageSize));
    if (filters.classification) params.set('classification', filters.classification);
    if (filters.profile) params.set('profile', filters.profile);
    if (debouncedSearch) params.set('search', debouncedSearch);
    if (sort.field !== DEFAULT_SORT_FIELD) params.set('sortField', sort.field);
    if (sort.direction !== 'asc') params.set('sortDirection', sort.direction);
    setSearchParams(params, { replace: true });
  }, [
    pagination.page,
    pagination.pageSize,
    filters.classification,
    filters.profile,
    debouncedSearch,
    sort.field,
    sort.direction,
    setSearchParams,
  ]);

  // Fetch the current page from the server whenever any request input changes.
  const fetchActivities = useCallback(async () => {
    try {
      setLoading(true);
      const res = await activityService.getActivitiesPaginated({
        classification: filters.classification || undefined,
        profile: filters.profile || undefined,
        search: debouncedSearch || undefined,
        sortField: sort.field,
        sortDirection: sort.direction,
        page: pagination.page,
        pageSize: pagination.pageSize,
      });
      setActivities(res.data);
      setPagination(prev => ({ ...prev, totalCount: res.totalCount, totalPages: res.totalPages }));
    } catch (error) {
      console.error('Error fetching activities:', error);
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  }, [
    filters.classification,
    filters.profile,
    debouncedSearch,
    sort.field,
    sort.direction,
    pagination.page,
    pagination.pageSize,
  ]);

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  const handleAddActivity = () => {
    setSelectedActivity(undefined);
    setIsModalVisible(true);
  };

  const handleEditActivity = async (activity: Activity) => {
    try {
      // Fetch full activity details including all costings
      const fullActivity = await activityService.getActivityById(activity.id);
      setSelectedActivity(fullActivity);
      setIsModalVisible(true);
    } catch (error) {
      console.error('Error fetching activity details:', error);
      // Fallback to using the activity from the list
      setSelectedActivity(activity);
      setIsModalVisible(true);
    }
  };

  const handleSaveActivity = async (activityData: Partial<Activity>) => {
    try {
      if (selectedActivity) {
        await activityService.updateActivity(selectedActivity.id, activityData);
      } else {
        await activityService.createActivity(activityData as CreateActivityData);
      }
      await fetchActivities();
      setIsModalVisible(false);
    } catch (error) {
      console.error('Error saving activity:', error);
      throw error;
    }
  };

  const handleDeleteActivity = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this activity?')) {
      try {
        await activityService.deleteActivity(id);
        await fetchActivities();
      } catch (error) {
        console.error('Error deleting activity:', error);
      }
    }
  };

  const handleFilterChange = (name: string, value: string) => {
    setFilters(prev => ({ ...prev, [name]: value }));
    setPagination(prev => ({ ...prev, page: DEFAULT_PAGE }));
  };

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPagination(prev => ({ ...prev, page: DEFAULT_PAGE }));
  };

  const handleClearFilters = () => {
    setFilters({ classification: '', profile: '' });
    setSearch('');
    setPagination(prev => ({ ...prev, page: DEFAULT_PAGE }));
  };

  const handleSortChange = (field: string) => {
    setSort(prev =>
      prev.field === field
        ? { field, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { field, direction: 'asc' }
    );
    setPagination(prev => ({ ...prev, page: DEFAULT_PAGE }));
  };

  const handlePageChange = (page: number) => {
    setPagination(prev => ({ ...prev, page }));
  };

  const handlePageSizeChange = (pageSize: number) => {
    setPagination(prev => ({ ...prev, pageSize, page: DEFAULT_PAGE }));
  };

  if (initialLoad) {
    return (
      <div className="tw-flex tw-justify-center tw-items-center" style={{ minHeight: '200px' }}>
        <Spinner role="status">Loading...</Spinner>
      </div>
    );
  }

  return (
    <div className="tw-w-full tw-p-4 tw-flex tw-flex-col tw-h-full tw-overflow-hidden">
      <PageHeader
        title="Activities"
        actions={[
          { key: 'refresh', label: 'Refresh', icon: 'bi-arrow-clockwise', intent: 'neutral', onClick: fetchActivities },
          {
            key: 'add',
            label: 'Add Activity',
            icon: 'bi-plus-lg',
            intent: 'primary',
            hidden: !hasPermission('activity:create'),
            onClick: handleAddActivity,
          },
        ]}
      />

      <div className="tw-shrink-0">
      <ActivityFilters
        filters={filters}
        search={search}
        onFilterChange={handleFilterChange}
        onSearchChange={handleSearchChange}
        onClearFilters={handleClearFilters}
        profiles={profiles}
      />
      </div>

      <div className="tw-grow table-scroll-wrapper tw-overflow-auto" style={{ minHeight: 0 }}>
      <ActivityTable
        activities={activities}
        sortField={sort.field}
        sortDirection={sort.direction}
        onSort={handleSortChange}
        onEditActivity={handleEditActivity}
        onDeleteActivity={handleDeleteActivity}
      />
      </div>

      <div className="tw-shrink-0">
      <ServerPagination
        currentPage={pagination.page}
        totalPages={pagination.totalPages}
        totalCount={pagination.totalCount}
        pageSize={pagination.pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        loading={loading}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        className="tw-mt-4"
      />
      </div>

      <ActivityModal
        visible={isModalVisible}
        onClose={() => setIsModalVisible(false)}
        onSave={handleSaveActivity}
        activity={selectedActivity}
      />
    </div>
  );
};

export default ActivityView;
